import React, { useState, useRef, useEffect } from 'react';
import api from '@/api';
import { HiOutlinePaperAirplane, HiComputerDesktop, HiOutlineUserCircle } from "react-icons/hi2";
import toast from 'react-hot-toast';

const ChatbotPage = () => {
  const [conversationId] = useState(() => {
    const stored = localStorage.getItem('conversationId');
    if (stored) return stored;
    const newId = crypto.randomUUID();
    localStorage.setItem('conversationId', newId);
    return newId;
  });
  const [messages, setMessages] = useState([
    { from: 'assistant', text: 'Bonjour ! Comment puis-je vous aider à trouver le profil idéal ou l\'offre parfaite ?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Fait défiler la vue vers le dernier message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const sendMessage = async (messageText) => {
    const text = messageText.trim();
    if (!text || isLoading) return;

    const userMessage = { from: 'user', text };
    setMessages(prev => [...prev, userMessage]);
    if (input) setInput('');
    setIsLoading(true);

    try {
      const response = await api.post('/chatbot', {
        message: text,
        conversationId,
        history: messages,
      });
      const assistantMessage = { from: 'assistant', text: response.data.reply };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = { from: 'assistant', text: 'Désolé, une erreur est survenue.' };
      setMessages(prev => [...prev, errorMessage]);
      toast.error("La communication avec l'assistant a échoué.");
      console.error("Erreur lors de l'appel au chatbot:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };
  
  const examplePrompts = [
    "Qui est dispo en juin avec au moins 3 ans d'expérience ?",
    "Quels candidats acceptent un TJM inférieur à 600€ ?",
    "Trouve des profils sur Paris avec un salaire minimum de 45k€",
  ];

  return (
    <div className="flex flex-col h-full bg-pro-dark-surface rounded-xl shadow-2xl">
      {/* Zone d'affichage des messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {messages.map((msg, index) => (
          <div key={index} className={`flex items-start gap-4 ${msg.from === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${msg.from === 'user' ? 'bg-pro-dark-accent' : 'bg-gray-500'}`}>
              {msg.from === 'user' ? <HiOutlineUserCircle size={20} /> : <HiComputerDesktop size={20} />}
            </div>
            <div className={`max-w-xl px-5 py-3 rounded-xl shadow-md ${msg.from === 'user' ? 'bg-pro-dark-accent text-white' : 'bg-pro-dark-border text-pro-dark-text-secondary'}`}>
              <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-500"><HiComputerDesktop size={20} /></div>
            <div className="max-w-xl px-5 py-3 rounded-xl bg-pro-dark-border text-pro-dark-text-secondary">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-75"></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-150"></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-300"></span>
              </div>
            </div>
          </div>
        )}
        
        {/* Affiche des exemples de prompts si la conversation n'a qu'un message */}
        {messages.length === 1 && !isLoading && (
            <div className="pt-8 text-center">
                <h2 className="text-lg font-semibold text-pro-dark-text-primary mb-4">Essayez une de ces questions :</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {examplePrompts.map((prompt, i) => (
                        <button key={i} onClick={() => sendMessage(prompt)} className="p-4 bg-pro-dark-border rounded-lg text-left text-sm text-pro-dark-text-secondary hover:bg-gray-600 transition-colors">
                            {prompt}
                        </button>
                    ))}
                </div>
            </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Formulaire de saisie amélioré */}
      <div className="p-4 border-t border-pro-dark-border">
        <form onSubmit={handleFormSubmit} className="relative">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Posez une question sur un candidat..."
            disabled={isLoading}
            rows={1}
            className="w-full p-4 pr-16 bg-pro-dark-bg border border-pro-dark-border rounded-lg resize-none focus:ring-2 focus:ring-pro-dark-accent focus:border-transparent"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleFormSubmit(e);
              }
            }}
          />
          <button type="submit" disabled={isLoading || !input.trim()} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-pro-dark-accent text-white hover:bg-pro-dark-accent-hover disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors">
            <HiOutlinePaperAirplane size={20} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatbotPage;